"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { signIn } from "next-auth/react";
import {
  createGuestConversation,
  checkEmailForSimulator,
  type CreateGuestConversationInput,
} from "@/app/_lib/actions/create-guest-conversation";
import { Send, Loader2, ArrowLeft, X, MessageCircle, LogIn, CalendarClock } from "lucide-react";
import { ServiceChips, type ChipItem } from "./service-chips";
import type { CoreServiceWithPlans } from "@/app/_lib/db/core-service";
import type { SolutionWithInclude } from "@/app/_lib/db/solutions";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { Logo } from "@/app/_components/icons";
import { useVisualViewportHeight } from "@/app/_hooks/use-visual-viewport-height";
import { cn } from "@/app/_lib/utils";
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from "@/app/_components/turnstile-widget";

type Step =
  | "email"
  | "email-exists"
  | "name"
  | "intent"
  | "description"
  | "submitting"
  | "done";

interface ChatMessage {
  id: string;
  from: "bot" | "user";
  content: string;
  renderChips?: boolean;
}

interface QueueItem {
  content: string;
  renderChips?: boolean;
  delay?: number;
}

export interface GuestChatSimulatorProps {
  open: boolean;
  onClose: () => void;
  services: Pick<CoreServiceWithPlans, "id" | "title">[];
  solutions: Pick<SolutionWithInclude, "id" | "headTitle">[];
  type?: "SALES" | "SUPPORT";
  initialChip?: ChipItem;
  botName?: string;
}

function uid() {
  return Math.random().toString(36).slice(2);
}

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50"
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

export interface SimulatorInnerProps
  extends Omit<GuestChatSimulatorProps, "open"> {
  onClose: () => void;
  onDone?: () => void;
  initialChip?: ChipItem;
  successPath?: string;
  botName?: string;
}

export function SimulatorInner({
  services,
  solutions,
  type,
  onClose,
  onDone,
  initialChip,
  successPath,
  botName = "Bex",
}: SimulatorInnerProps) {
  const {
    data: session,
    status: sessionStatus,
    update: updateSession,
  } = useSession();
  const router = useRouter();

  const sessionReady = sessionStatus !== "loading";
  const isAuth = sessionReady && !!session?.user;
  const authEmail = session?.user?.email ?? "";
  const authFirstName = (session?.user as any)?.firstname ?? "";
  const authLastName = (session?.user as any)?.lastname ?? "";

  // Start in a waiting state — init effect sets the real step once session is resolved
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [step, setStep] = useState<Step>("email");
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [error, setError] = useState("");

  const emailRef = useRef(isAuth ? authEmail : "");
  const firstNameRef = useRef(isAuth ? authFirstName : "");
  const lastNameRef = useRef(isAuth ? authLastName : "");
  const intentRef = useRef(initialChip?.label ?? "");
  const selectedChipRef = useRef<ChipItem | null>(initialChip ?? null);
  // Last submitted description — kept so a post-submit "email already has an
  // account" retry can resubmit without making the guest retype everything.
  const descriptionRef = useRef("");
  // True while we've bounced the guest back to the email step specifically to
  // collect a different email after a final-submit "email already exists"
  // failure — name/intent/description are already known, so once a valid new
  // email comes in we resubmit directly instead of re-asking for them.
  const pendingEmailRetryRef = useRef(false);
  const botQueueRef = useRef<QueueItem[]>([]);

  // Bot mitigation for the first submit (createGuestConversation) — the widget
  // mounts as soon as the simulator opens so a token is ready well before the
  // user reaches the submit step.
  const [turnstileToken, setTurnstileToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const formRenderedAtRef = useRef(Date.now());
  const pendingAutoLoginRef = useRef<{
    email: string;
    token: string;
    feedbackId: string;
    destination: string;
  } | null>(null);

  // Bumped after submitConversation writes to the refs so the queue effect re-runs
  const [submitTick, setSubmitTick] = useState(0);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initDoneRef = useRef(false);

  // Sets scrollTop directly on the known scroll container instead of
  // Element.scrollIntoView() on a descendant. scrollIntoView lets the browser
  // walk up and decide which ancestors need to move to bring the target into
  // view — on real iOS/Android that can end up nudging the document/layout
  // viewport, which is what toggles the address bar and yanks the whole
  // fixed-position shell. Setting scrollTop on one specific element can never
  // touch anything outside it.
  const scrollToBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const addMessage = useCallback((msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  // Shows the typing-dots bubble for `dotsDelay`, then the message appears
  // whole — like a normal chat, not a per-character typewriter. The
  // typewriter used to mutate the DOM on every character (~60x/sec) while
  // "typing," which is the strongest remaining suspect for the real-device
  // layout jump: on iOS/Android that much rapid reflow while the bubble grows
  // is exactly the kind of activity that can get misread as a scroll gesture
  // and toggle the browser's address bar mid-conversation.
  const botSay = useCallback(
    (content: string, renderChips = false, dotsDelay = 900) => {
      const id = uid();
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage({ id, from: "bot", content, renderChips });
      }, dotsDelay);
    },
    [addMessage],
  );

  // Queue processor + auto-login trigger
  // NOTE: submitTick in deps is critical — submitConversation writes to refs (not state),
  // so without this tick the effect would never re-run after the API call returns.
  useEffect(() => {
    console.log("[Queue] effect fired", {
      isTyping,
      queueLen: botQueueRef.current.length,
      hasPendingLogin: !!pendingAutoLoginRef.current,
      submitTick,
    });

    if (isTyping) return;

    if (botQueueRef.current.length > 0) {
      const next = botQueueRef.current.shift()!;
      console.log(
        "[Queue] processing next message:",
        next.content.slice(0, 50),
      );
      const timer = setTimeout(() => {
        botSay(next.content, next.renderChips, next.delay ?? 700);
      }, 280);
      return () => clearTimeout(timer);
    }

    if (pendingAutoLoginRef.current) {
      const info = pendingAutoLoginRef.current;
      pendingAutoLoginRef.current = null;
      console.log("[Queue] starting auto-login for", info.email);
      (async () => {
        try {
          const res = await signIn("guest-token", {
            token: info.token,
            redirect: false,
          });
          console.log("[Queue] signIn result:", JSON.stringify(res));
          if (res?.ok) {
            if (typeof window !== "undefined") {
              localStorage.setItem("needsProfileSetup", "true");
            }
            console.log(
              "[Queue] auto-login ok — navigating to",
              info.destination,
            );
            // update() refreshes the client-side session context,
            // refresh() invalidates the RSC cache so server components re-read the new cookie,
            // then push() does a normal client-side navigation.
            await updateSession();
            router.push(info.destination);
            router.refresh();

            // onClose();
            return;
          }
          console.warn("[Queue] signIn not ok:", res?.error);
        } catch (err) {
          console.error("[Queue] signIn threw:", err);
        }
        // Auto-login failed — show done state
        setStep("done");
        onDone?.();
        botSay(
          `Your account is ready! Check ${info.email} for your setup link. Sign in anytime to track this conversation.`,
          false,
          400,
        );
      })();
    }
  }, [isTyping, botSay, router, onClose, onDone, submitTick, updateSession]);

  // Init — wait until the session status is resolved before starting
  useEffect(() => {
    if (!sessionReady) return;
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    // Compute correct first step now that auth state is known
    const fStep: Step = isAuth
      ? initialChip
        ? "description"
        : "intent"
      : "email";

    setMessages([]);
    setStep(fStep);
    setInput("");
    setError("");
    emailRef.current = isAuth ? authEmail : "";
    firstNameRef.current = isAuth ? authFirstName : "";
    lastNameRef.current = isAuth ? authLastName : "";
    botQueueRef.current = [];
    pendingAutoLoginRef.current = null;

    if (isAuth) {
      if (initialChip) {
        addMessage({ id: uid(), from: "user", content: initialChip.label });
        const prompt =
          type === "SUPPORT"
            ? `Got it! Tell us what's happening with ${initialChip.label}. Any context helps us get to the right person fast.`
            : `Great choice! Tell us more about your ${initialChip.label} project: timeline, budget, any specific requirements?`;
        botSay(prompt, false, 800);
      } else {
        botSay(
          type === "SUPPORT"
            ? `Hi, ${authFirstName || "there"}! 👋 What are you having issues with today?`
            : `Welcome back, ${authFirstName || "there"}! What would you like to work on with us?`,
          true,
          800,
        );
      }
    } else {
      // Unauthenticated intro — queue 2–3 opening messages before email step
      if (type === "SUPPORT") {
        botSay(
          `Hi! I'm ${botName} 👋, here to help get your issue sorted.`,
          false,
          600,
        );
        botQueueRef.current = [
          {
            content:
              "Our support team responds within 2 hours. I'll need a couple of quick details.",
            delay: 700,
          },
          { content: "What's your email address?", delay: 600 },
        ];
      } else if (initialChip) {
        botSay(
          `Hi! I'm ${botName} 👋, let's get your ${initialChip.label} project started at Busyexpand!`,
          false,
          600,
        );
        botQueueRef.current = [
          {
            content:
              "What's your email? I'll use it to set up your project workspace.",
            delay: 700,
          },
        ];
      } else {
        botSay(
          `Hi! I'm ${botName} 👋, your project guide at Busyexpand.`,
          false,
          600,
        );
        botQueueRef.current = [
          {
            content:
              "Here's how it works: you share what you need → we scope it with our team → you get a response within 2 hours. ⚡",
            delay: 900,
          },
          {
            content:
              "First things first, what's your email so I can set up your workspace?",
            delay: 700,
          },
        ];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady]);

  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isTyping]);

  const handleChipSelect = (chip: ChipItem) => {
    selectedChipRef.current = chip;
    intentRef.current = chip.label;
    addMessage({ id: uid(), from: "user", content: chip.label });
    setInput("");
    setStep("description");
    const prompt =
      type === "SUPPORT"
        ? "Got it! Describe the issue in as much detail as you can: any error messages, steps to reproduce, or history helps a lot."
        : "Tell us a bit more: timeline, budget, or anything our team should know upfront.";
    botSay(prompt, false, 700);
  };

  const submitConversation = async (description: string) => {
    const firstName = firstNameRef.current || authFirstName;
    const parts = firstName.split(" ");
    const first = parts[0] || "Guest";
    const last =
      lastNameRef.current || authLastName || parts.slice(1).join(" ") || "";
    const chip = selectedChipRef.current;

    const fullIntent = intentRef.current
      ? `${intentRef.current}: ${description}`
      : description;

    const payload: CreateGuestConversationInput = {
      email: emailRef.current || authEmail,
      firstName: first,
      lastName: last,
      intent: fullIntent,
      type: type ?? "SALES",
      ...(chip?.type === "service" ? { serviceId: chip.id } : {}),
      ...(chip?.type === "solution" ? { solutionId: chip.id } : {}),
      turnstileToken,
      // This chat's TurnstileWidget always runs in mode="invisible".
      invisibleTurnstile: true,
      honeypot,
      formRenderedAt: formRenderedAtRef.current,
    };

    console.log(
      "[Simulator] calling createGuestConversation for",
      payload.email,
    );

    const result = await createGuestConversation(
      payload,
      typeof window !== "undefined" ? window.location.origin : "",
    );

    console.log("[Simulator] createGuestConversation returned:", {
      success: result.success,
      feedbackId: result.feedbackId,
      isNewUser: (result as any).isNewUser,
      hasAutoLoginToken: !!(result as any).autoLoginToken,
      message: (result as any).message,
    });

    if (!result.success || !result.feedbackId) {
      // A guest's email turned out to already belong to a real account
      // (created between their earlier email-step check and this final
      // submit, or an account the earlier check missed) — reuse the same
      // "email-exists" CTA (sign in, or use a different email) instead of
      // the generic "something went wrong, try the description again"
      // message, which would just fail identically on every retry. Name,
      // intent, and description are already known (refs + descriptionRef):
      // if they pick "use a different email" and it's valid this time, we
      // resubmit directly rather than re-asking questions they already
      // answered.
      if (!isAuth && (result as any).emailExists) {
        pendingEmailRetryRef.current = true;
        setError("");
        setStep("email-exists");
        botSay(
          "Looks like that email's already tied to an account. Sign in to continue, or use a different email.",
          false,
          300,
        );
        turnstileRef.current?.reset();
        return;
      }

      setError(result.message || "Something went wrong. Please try again.");
      setStep("description");
      botSay("Sorry, something went wrong. Could you try again?", false, 300);
      turnstileRef.current?.reset();
      return;
    }

    if (isAuth) {
      const destination = successPath
        ? `${successPath}/${result.feedbackId}`
        : `/dashboard/messages/${result.feedbackId}`;
      console.log("[Simulator] auth user redirect to:", destination);
      // refresh() clears the RSC cache so the new conversation shows,
      // then push() does client-side navigation.
      router.push(destination);
      router.refresh();
      // onClose();
      return;
    }

    // Non-authed success — queue messages then auto-login.
    // We call setSubmitTick AFTER writing to the refs so the queue effect re-runs.
    // Without this, the effect already fired while "Give me just a moment" was showing
    // (refs were empty then) and won't re-run since refs don't trigger renders.
    const destination = successPath
      ? `${successPath}/${result.feedbackId}`
      : `/dashboard/messages/${result.feedbackId}`;

    botQueueRef.current = [
      {
        content: `${type === "SUPPORT" ? "Support request" : "Project brief"} received! 🎉 Our team will get back to you within 2 hours.`,
        delay: 400,
      },
    ];

    if (result.autoLoginToken) {
      botQueueRef.current.push({
        content:
          "Setting up your dashboard so you can track this conversation in real time...",
        delay: 700,
      });
      pendingAutoLoginRef.current = {
        email: emailRef.current,
        token: result.autoLoginToken,
        feedbackId: result.feedbackId,
        destination,
      };
      console.log(
        "[Simulator] refs set — queue len:",
        botQueueRef.current.length,
        "pendingLogin:",
        !!pendingAutoLoginRef.current,
      );
    } else {
      botQueueRef.current.push({
        content: `Check ${emailRef.current}. We've sent a link to activate your account and continue the conversation.`,
        delay: 700,
      });
      setStep("done");
      onDone?.();
    }

    // Bump tick to trigger the queue effect (refs don't cause re-renders on their own)
    setSubmitTick((t) => t + 1);
  };

  const handleSubmit = async () => {
    const trimmed = input.trim();
    setError("");
    botQueueRef.current = []; // Clear any pending intro messages when user acts

    if (step === "email") {
      if (!isValidEmail(trimmed)) {
        setError("That doesn't look like a valid email. Please try again.");
        return;
      }
      emailRef.current = trimmed;
      addMessage({ id: uid(), from: "user", content: trimmed });
      setInput("");
      setIsCheckingEmail(true);
      try {
        const check = await checkEmailForSimulator(trimmed.toLowerCase());
        if (check.exists && check.hasPassword) {
          setStep("email-exists");
          botSay(
            "Hey, there's already an account with that email. Sign in to pick up your conversations!",
            false,
            500,
          );
          return;
        }
      } finally {
        setIsCheckingEmail(false);
      }

      if (pendingEmailRetryRef.current) {
        // We already have name/intent/description from before the
        // "email already exists" failure — resubmit directly instead of
        // re-asking questions the guest already answered.
        pendingEmailRetryRef.current = false;
        setStep("submitting");
        botSay("Perfect, let me get that submitted now... 🔄", false, 400);
        await submitConversation(descriptionRef.current);
        return;
      }

      setStep("name");
      botSay("Nice! What's your name?", false, 600);
      return;
    }

    if (step === "name") {
      if (!trimmed) {
        setError("Please enter your name.");
        return;
      }
      firstNameRef.current = trimmed;
      addMessage({ id: uid(), from: "user", content: trimmed });
      setInput("");
      const firstName = trimmed.split(" ")[0];
      if (initialChip) {
        addMessage({ id: uid(), from: "user", content: initialChip.label });
        setStep("description");
        const prompt =
          type === "SUPPORT"
            ? `Got it, ${firstName}! Tell us what's happening. Be as specific as you can.`
            : `Great to meet you, ${firstName}! Tell us more about your ${initialChip.label} project.`;
        botSay(prompt, false, 700);
      } else {
        setStep("intent");
        const prompt =
          type === "SUPPORT"
            ? `Got it, ${firstName}! What area are you having issues with?`
            : `Nice to meet you, ${firstName}! What would you like to work on with us?`;
        botSay(prompt, true, 700);
      }
      return;
    }

    if (step === "intent") {
      if (!trimmed) return;
      intentRef.current = trimmed;
      addMessage({ id: uid(), from: "user", content: trimmed });
      setInput("");
      setStep("description");
      const prompt =
        type === "SUPPORT"
          ? "Understood. Describe the issue in detail: any error messages, steps to reproduce, or relevant history really help."
          : "Tell us more: timeline, budget, or anything specific our team should know upfront.";
      botSay(prompt, false, 700);
      return;
    }

    if (step === "description") {
      if (!trimmed) return;
      descriptionRef.current = trimmed;
      addMessage({ id: uid(), from: "user", content: trimmed });
      setInput("");
      setStep("submitting");
      botSay(
        "Give me just a moment while I pull everything together... 🔄",
        false,
        400,
      );
      await submitConversation(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isInputDisabled =
    !sessionReady ||
    step === "submitting" ||
    step === "done" ||
    step === "email-exists" ||
    isCheckingEmail;

  const placeholder =
    step === "email"
      ? "you@example.com"
      : step === "name"
        ? "Your full name"
        : step === "intent"
          ? type === "SUPPORT"
            ? "Describe area (or pick above)"
            : "Describe project (or pick above)"
          : step === "description"
            ? type === "SUPPORT"
              ? "Describe the issue in detail…"
              : "Timeline, budget, requirements…"
            : "";

  const userInitial = (firstNameRef.current ||
    authFirstName ||
    "U")[0]?.toUpperCase();

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Chat header — matches ChatDetail style */}
      <div className="flex items-center gap-3 px-4 h-[56px] border-b border-border flex-shrink-0 bg-background">
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent transition-colors flex-shrink-0"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-border">
          <div className="w-full h-full bg-primary flex items-center justify-center">
            <MessageCircle size={14} className="text-white" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">New Conversation</p>
          <p className="text-[10px] text-muted-foreground capitalize">
            {(type ?? "SALES").toLowerCase()} enquiry
          </p>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3 bg-background"
      >
        {/* While session resolves, show typing indicator */}
        {!sessionReady && (
          <div className="flex items-end gap-2">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <MessageCircle size={13} className="text-white" />
            </div>
            <div className="bg-accent rounded-[20px] rounded-bl-none shadow-sm">
              <TypingDots />
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex items-end gap-2 w-full ${msg.from === "user" ? "flex-row-reverse" : "flex-row"}`}
            >
              <div className="flex-shrink-0 mb-1">
                {msg.from === "bot" ? (
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                    <MessageCircle size={13} className="text-white" />
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-accent text-foreground flex items-center justify-center text-[10px] font-bold border border-border">
                    {userInitial}
                  </div>
                )}
              </div>
              <div
                className={`flex flex-col flex-1 min-w-0 ${msg.from === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[78%] px-3 py-2 rounded-[20px] text-sm leading-relaxed shadow-sm break-words ${
                    msg.from === "user"
                      ? "bg-primary text-white rounded-br-none"
                      : "bg-accent text-foreground rounded-bl-none"
                  }`}
                >
                  {msg.content}
                </div>
                {msg.renderChips && (
                  <div className="mt-2 max-w-[90%]">
                    <ServiceChips
                      services={services}
                      solutions={solutions}
                      onSelect={handleChipSelect}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {/* Typing dots bubble */}
          {(isTyping || isCheckingEmail) && (
            <motion.div
              key="typing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-end gap-2"
            >
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <MessageCircle size={13} className="text-white" />
              </div>
              <div className="bg-accent rounded-[20px] rounded-bl-none shadow-sm">
                <TypingDots />
              </div>
            </motion.div>
          )}

          {/* Submitting loader (only visible when API call is still running) */}
          {step === "submitting" &&
            !isTyping &&
            botQueueRef.current.length === 0 &&
            !pendingAutoLoginRef.current && (
              <motion.div
                key="hold"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-end gap-2"
              >
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={13} className="text-white" />
                </div>
                <div className="px-3 py-2.5 rounded-[20px] rounded-bl-none shadow-sm bg-accent text-foreground flex items-center gap-2">
                  <Loader2
                    size={12}
                    className="animate-spin text-primary flex-shrink-0"
                  />
                  <span className="text-sm">Pulling everything together…</span>
                </div>
              </motion.div>
            )}

          {/* Email-exists CTA */}
          {step === "email-exists" && !isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-3 pt-2"
            >
              <Link
                href={`/login?redirect=/dashboard/messages`}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-full hover:opacity-90 transition-opacity"
              >
                <LogIn size={14} />
                Sign in to your account
              </Link>
              <button
                onClick={() => {
                  emailRef.current = "";
                  setStep("email");
                  setInput("");
                  botSay(
                    "No problem! What email would you like to use?",
                    false,
                    300,
                  );
                }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Use a different email
              </button>
            </motion.div>
          )}

          {/* Done CTA for non-auto-login case */}
          {step === "done" && !isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-3 pt-4"
            >
              <button
                onClick={() => {
                  window.open(`mailto:${emailRef.current}`, "_blank");
                  onClose();
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity"
              >
                Open your inbox
              </button>
              <Link
                href="/dashboard/bookings?book=1"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-border text-foreground text-sm font-medium rounded-full hover:bg-accent transition-colors"
              >
                <CalendarClock size={14} /> Book a call instead
              </Link>
              <Link
                href="/login?redirect=/dashboard/messages"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Already set up? Sign in →
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {error && (
        <p className="px-4 py-1.5 text-xs text-red-500 bg-red-50 border-t border-red-100">
          {error}
        </p>
      )}

      {/* Input */}
      <div className="px-3 py-3 border-t border-border bg-background">
        <div className="flex items-center gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`;
            }}
            onKeyDown={handleKeyDown}
            disabled={isInputDisabled}
            placeholder={isInputDisabled ? "" : placeholder}
            rows={1}
            className={cn(
              "flex-1 resize-none overflow-hidden text-sm bg-muted/5 border border-border rounded-full pl-4 pr-5 py-3 outline-none focus:border-primary transition-colors placeholder:text-muted-foreground placeholder:whitespace-nowrap disabled:opacity-40 max-h-[96px]",
             
            )}
          />
          <button
            onClick={handleSubmit}
            disabled={isInputDisabled || !input.trim()}
            className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-primary text-white rounded-full disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            <Send size={15} />
          </button>
        </div>
        <div className="mt-2 flex justify-center">
          <Link
            href="/dashboard/bookings?book=1"
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors"
          >
            <CalendarClock size={12} /> Prefer to talk? Book a call
          </Link>
        </div>
      </div>

      {/* Honeypot — hidden via CSS (not type="hidden") so bots that fill
          every field in the DOM still trip it. Real users never see it. */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="guest-chat-website">Website</label>
        <input
          id="guest-chat-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <TurnstileWidget
        ref={turnstileRef}
        onVerify={setTurnstileToken}
        mode="invisible"
      />
    </div>
  );
}

export default function GuestChatSimulator({
  open,
  onClose,
  services,
  solutions,
  type = "SALES",
  initialChip,
  botName = "Bex",
}: GuestChatSimulatorProps) {
  const [isDone, setIsDone] = useState(false);

  // Track the visual viewport so the overlay follows the on-screen keyboard
  // instead of being pushed off-screen (the "flies up and goes blank" bug).
  // This component is mounted once by its parent and toggled via `open`
  // rather than mounted fresh each time — pass `open` so the measurement
  // re-runs on every open instead of using a stale one from page load.
  useVisualViewportHeight(open);

  useEffect(() => {
    if (open) setIsDone(false);
  }, [open]);

  // Lock the underlying page from scrolling while the full-screen chat is open,
  // so focusing the input can't scroll the body behind the overlay.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed left-0 right-0 top-0 z-40 bg-background flex flex-col overflow-hidden transition-transform duration-150 ease-out"
      style={{
        height: "var(--vvh, 100dvh)",
        transform: "translateY(var(--vv-offset-top, 0px))",
      }}
    >
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 h-[56px] border-b border-border flex-shrink-0 bg-background">
        <div className="text-[30px] leading-none">
          <Logo />
        </div>
        <div className="flex-1" />
        {!isDone && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent transition-colors text-muted-foreground"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar (desktop only) */}
        <div className="hidden md:flex flex-col w-[280px] border-r border-border flex-shrink-0">
          <div className="px-4 py-4 border-b border-border">
            <h2 className="font-semibold text-sm">Messages</h2>
          </div>
          <div className="flex items-center gap-3 px-4 py-3.5 bg-primary/8 border-b border-border">
            <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">New Conversation</p>
              <div className="flex items-center justify-between gap-1 mt-0.5">
                <p className="text-xs text-muted-foreground truncate">
                  Starting now…
                </p>
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    type === "SUPPORT"
                      ? "bg-blue-50 text-blue-600"
                      : "bg-accent text-primary"
                  }`}
                >
                  {type === "SUPPORT" ? "Support" : "Service"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex flex-col min-h-0">
          <SimulatorInner
            services={services}
            solutions={solutions}
            type={type}
            onClose={onClose}
            onDone={() => setIsDone(true)}
            initialChip={initialChip}
            botName={botName}
          />
        </div>
      </div>
    </div>
  );
}
